import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckEditAction, CheckDeleteAction, GetAccess, GetRole } from "./roles";
import { useDeleteData } from "../Hooks/useDeleteData";
import ModalContainer from "../Others/ModalContainer";
import ModalEmail from "../Others/ModalForEmail";
import EmailModal from "./EmailModel";
import {
  FaCheckCircle,
  FaHourglassHalf,
  FaTimesCircle,
  FaClipboardCheck,
  FaRegFileAlt,
  FaEnvelope,
  FaEnvelopeOpen,
} from "react-icons/fa";

const ActionButtons = ({
  row,
  data,
  viewUrl,
  viewLink,
  editUrl,
  editLink,
  canEdit = false,
  canDelete = false,
  onDelete,
  onToggleRead,
  isTogglingRead = false,
  viewState,
  editState,
  moduleName = "",
  emailRequired = false,
  EmailRequired = false,
  emailEnable = false,
  isEmailSent = false,
  onEmailClick,
  fetcher,
  id,
  removevalue,
  url,
  emailType,
  EditAccess = "",
  DeteleAccess = "",
  pageIndex,
  searchQuery,
  from,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const rowData = row || data;
  const itemId = id !== undefined ? id : (rowData?.id || rowData?.customerId || rowData?.vendorId || rowData?.coId || rowData?.paymentRequestId || rowData?.contractId || rowData?.prId || rowData?.poId);
  const entityName = removevalue || rowData?.ref || rowData?.companyName || rowData?.taskName || rowData?.contractTitle || rowData?.title || "";
  const finalModuleName = moduleName;
  const finalEditAccess = EditAccess || (finalModuleName ? `${finalModuleName.toLowerCase().replace(/ /g, "_")}_edit` : "");
  const finalDeleteAccess = DeteleAccess || (finalModuleName ? `${finalModuleName.toLowerCase().replace(/ /g, "_")}_delete` : "");

  const isEditable = CheckEditAction(rowData, finalModuleName, canEdit || (finalEditAccess ? GetAccess(finalEditAccess) : false));
  const isDeletable = CheckDeleteAction(rowData, canDelete || (finalDeleteAccess ? GetAccess(finalDeleteAccess) : false)) || GetRole();

  const finalViewUrl = viewUrl || (viewLink ? `${viewLink}${itemId}` : "");

  const EditCondition = editLink?.includes("{id}") ? true : false;
  const editableLink = EditCondition ? editLink.replace("{id}", `${itemId}`) : "";
  const finalEditUrl = editUrl || (editLink ? (EditCondition ? editableLink : `${editLink}${itemId}`) : "");

  const finalEmailRequired = emailRequired || EmailRequired;

  const getStatusIcon = () => {
    if (!rowData || ["Sales-Orders", "Suppliers", "Sales Orders"].includes(finalModuleName)) {
      return null;
    }
    const status = rowData?.approvalStatus?.toLowerCase();
    if (status === "approved" || status === "completed") {
      return <FaCheckCircle className="text-green-500 w-4 h-4" title="Approved" />;
    }
    if (status === "pending" || status === "new") {
      return <FaHourglassHalf className="text-yellow-500 w-4 h-4" title="Pending" />;
    }
    if (status === "rejected" || status === "declined") {
      return <FaTimesCircle className="text-red-500 w-4 h-4" title={rowData?.approvalStatus} />;
    }
    if (status === "verified") {
      return <FaClipboardCheck className="text-purple-500 w-4 h-4" title="Verified" />;
    }
    if (status === "reviewed") {
      return <FaRegFileAlt className="text-indigo-500 w-4 h-4" title="Reviewed" />;
    }
    return null;
  };

  const { handle } = useDeleteData(fetcher);
  const handleDeleteClick = () => {
    if (onDelete) {
      onDelete(rowData);
    } else if (fetcher && url) {
      setIsModalOpen(true);
    }
  };

  const executeDelete = async () => {
    if (fetcher && url) {
      await handle(url + `${itemId}`, setIsModalOpen);
    }
  };

  const handleEmailClick = () => {
    if (onEmailClick) {
      onEmailClick(rowData);
    } else {
      setModalOpen(true);
    }
  };

  const finalViewState = viewState || {
    data: rowData,
    pageIndex: pageIndex,
    searchQuery: searchQuery !== undefined ? searchQuery : "",
  };

  const finalEditState = editState || {
    data: rowData,
    from: from || window.location.pathname,
    pageIndex: pageIndex,
    searchQuery: searchQuery !== undefined ? searchQuery : "",
  };

  return (
    <>
      <div className="flex items-center gap-2 justify-center">
        {/* Toggle Read / Unread */}
        {onToggleRead && (
          <button
            type="button"
            onClick={() => onToggleRead(rowData?.prId || itemId, rowData?.isRead)}
            disabled={isTogglingRead}
            className={`border p-1.5 rounded-md transition-all duration-200 flex items-center justify-center cursor-pointer ${rowData?.isRead
                ? "border-gray-200 text-gray-400 hover:text-purple-600 hover:border-purple-200 hover:bg-purple-50"
                : "border-purple-300 bg-purple-50 text-purple-600 hover:bg-purple-100 hover:border-purple-400 shadow-xs"
              }`}
            title={rowData?.isRead ? "Mark as unread" : "Mark as read"}
          >
            {isTogglingRead ? (
              <svg className="w-3.5 h-3.5 animate-spin text-purple-600" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            ) : rowData?.isRead ? (
              <FaEnvelopeOpen className="w-3.5 h-3.5" />
            ) : (
              <FaEnvelope className="w-3.5 h-3.5" />
            )}
          </button>
        )}

        {/* Status Icon */}
        {getStatusIcon() && (
          <div className="flex justify-center items-center mr-1">
            {getStatusIcon()}
          </div>
        )}

        {/* View */}
        {finalViewUrl && (
          <Link
            to={finalViewUrl}
            state={finalViewState}
            className="border border-blue-200 text-blue-600 hover:bg-blue-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
            title="View"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          </Link>
        )}

        {/* Email */}
        {finalEmailRequired && !isEmailSent && !rowData?.purchaser && emailEnable && (
          <button
            onClick={handleEmailClick}
            className="border border-purple-200 text-purple-600 hover:bg-purple-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
            title="Send Email"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </button>
        )}

        {/* Edit */}
        {isEditable && finalEditUrl && (
          <Link
            to={finalEditUrl}
            state={finalEditState}
            className="border border-sky-200 text-sky-500 hover:bg-sky-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
            title="Edit"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z"
              />
            </svg>
          </Link>
        )}

        {/* Delete */}
        {isDeletable && (
          <button
            onClick={handleDeleteClick}
            className="border border-red-200 text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
            title="Delete"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {isModalOpen && (
        <ModalContainer
          visiable={isModalOpen}
          title="Warning"
          content={`Are you sure, ${entityName} will be removed from ${finalModuleName}!`}
          handleClick={executeDelete}
          closeModal={() => setIsModalOpen(false)}
        />
      )}

      {/* Email Modal */}
      {modalOpen && (
        <ModalEmail
          visiable={modalOpen}
          title=""
          hasContent={true}
          content={
            <EmailModal
              fetcher={fetcher}
              vendorId={rowData?.vendor || rowData?.vendorId}
              id={rowData?.id || itemId}
              url={url}
              idd={itemId}
              onClose={() => setModalOpen(false)}
              emailType={emailType}
            />
          }
          hasButton={false}
          closeModal={() => setModalOpen(false)}
        />
      )}
    </>
  );
};

export default ActionButtons;

function ViewButton({ viewLink, viewState }) {
  return (
    <Link
      to={viewLink}
      state={viewState}
      className="border border-gray-200 text-gray-600 hover:bg-gray-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
      title="View Details"
    >
      <svg
        className="w-3.5 h-3.5 flex-shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
        />
      </svg>
      <span className="hidden sm:inline">View</span>
    </Link>
  )
}

function EditButton({ editLink, editState }) {
  return (
    <Link
      to={editLink}
      state={editState}
      className="border border-gray-200 text-gray-600 hover:bg-gray-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
      title="Edit"
    >
      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
      <span className="hidden sm:inline">Edit</span>
    </Link>
  )
}

function DeleteButton({ onDelete }) {
  return (
    <button
      onClick={onDelete}
      className="border border-red-100 text-red-500 hover:bg-red-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap cursor-pointer"
      title="Delete"
    >
      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
      <span className="hidden sm:inline">Delete</span>
    </button>
  );
}

function ToggleReadButton({ isRead, onToggle, isLoading }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={isLoading}
      className={`border px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap cursor-pointer ${isRead
          ? "border-gray-200 text-gray-400 hover:text-purple-600 hover:border-purple-200 hover:bg-purple-50"
          : "border-purple-300 bg-purple-50 text-purple-600 hover:bg-purple-100 hover:border-purple-400 shadow-xs"
        }`}
      title={isRead ? "Mark as unread" : "Mark as read"}
    >
      {isLoading ? (
        <svg className="w-3.5 h-3.5 animate-spin text-purple-600 flex-shrink-0" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
      ) : isRead ? (
        <FaEnvelopeOpen className="w-3.5 h-3.5 flex-shrink-0" />
      ) : (
        <FaEnvelope className="w-3.5 h-3.5 flex-shrink-0" />
      )}
      <span className="hidden sm:inline">{isRead ? "Read" : "Unread"}</span>
    </button>
  );
}

export { ViewButton, EditButton, DeleteButton, ToggleReadButton };